import { Play } from "lucide-react";
import { useState } from "react";
import type { HallOfFameTileEntry } from "@/components/hall-of-fame/HallOfFameTile";
import {
  hofPreviewUrl,
  isHofUploadedVideo,
  isHofYouTubeMedia,
  type HofMediaType,
} from "@/lib/hall-of-fame.helpers";
import { parseYouTubeId, youtubeEmbedUrl } from "@/lib/youtube";

type HallOfFameDetailProps = {
  entry: HallOfFameTileEntry & { body: string };
};

export function HallOfFameDetail({ entry }: HallOfFameDetailProps) {
  const mediaType: HofMediaType = entry.media_type === "video" ? "video" : "image";
  const isYoutube = isHofYouTubeMedia(mediaType, entry.media_url);
  const isUploadedVideo = isHofUploadedVideo(mediaType, entry.media_url);
  const youtubeId = isYoutube ? parseYouTubeId(entry.media_url) : null;
  const [youtubePlaying, setYoutubePlaying] = useState(false);

  return (
    <div className="grid gap-8 border-t border-white/10 pt-10 md:grid-cols-5 md:gap-12">
      <div className="md:col-span-3">
        <div className="overflow-hidden border border-white/10 bg-black">
          {mediaType === "image" ? (
            <img
              src={entry.media_url}
              alt={entry.title}
              className="aspect-video w-full object-cover"
            />
          ) : null}

          {isUploadedVideo ? (
            <video
              key={entry.id}
              src={entry.media_url}
              className="aspect-video w-full bg-black object-contain"
              controls
              playsInline
              preload="metadata"
            />
          ) : null}

          {youtubeId ? (
            youtubePlaying ? (
              <iframe
                key={entry.id}
                title={entry.title}
                src={`${youtubeEmbedUrl(youtubeId)}?autoplay=1`}
                className="aspect-video w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                className="group relative block aspect-video w-full"
                onClick={() => setYoutubePlaying(true)}
              >
                <img
                  src={hofPreviewUrl("video", entry.media_url)}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <span className="absolute inset-0 bg-ink/35" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/50 bg-black/45 text-white transition group-hover:scale-105">
                    <Play className="h-7 w-7 fill-current" />
                  </span>
                </span>
              </button>
            )
          ) : null}
        </div>
      </div>

      <div className="md:col-span-2">
        <div className="font-display text-sm tracking-[0.2em] text-ember">{entry.entry_year}</div>
        <h2 className="mt-2 font-display text-4xl leading-none md:text-5xl">{entry.title}</h2>
        {entry.subtitle ? (
          <p className="mt-2 text-sm uppercase tracking-[0.16em] text-foreground/60">
            {entry.subtitle}
          </p>
        ) : null}
        {entry.body.trim() ? (
          <p className="mt-5 whitespace-pre-wrap text-foreground/80">{entry.body}</p>
        ) : null}
      </div>
    </div>
  );
}
